import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CaretDown,
  CaretRight,
  CircleNotch,
  WarningCircle,
} from '@/components/icons'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useVisionClient } from '@/app/vision-client-provider'
import { useTenantStore } from '@/app/state/tenant-store'
import { cn } from '@/lib/cn'
import { num } from '@/lib/format'
import { VisionApiError } from '@/lib/vision/client'
import type { Order, ValidationRun } from '@/lib/vision/schemas'
import { BulkAckConfirmDialog } from './bulk-ack-confirm-dialog'
import {
  applyPastedColumn,
  buildBulkAckLines,
  defaultQtyDrafts,
  draftsDifferFromDefaults,
  planForLoadedPo,
  setAllEditableDrafts,
  summarizeBulkAck,
  type BulkAckLoadedPo,
  type BulkAckQtyDraftByKey,
} from './bulk-ack-model'
import {
  freshIdempotencyKey,
  runBulkAcknowledgements,
  type BulkAckPoJob,
} from './bulk-ack-submit'

type LoadState = 'loading' | 'ready' | 'error'

export function BulkAckDialog({
  open,
  orders,
  onOpenChange,
  onFinished,
}: {
  open: boolean
  orders: readonly Order[]
  onOpenChange: (open: boolean) => void
  onFinished: () => void
}) {
  const client = useVisionClient()
  const tenantId = useTenantStore(s => s.tenantId)
  const queryClient = useQueryClient()
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState<BulkAckLoadedPo[]>([])
  const [drafts, setDrafts] = useState<BulkAckQtyDraftByKey>({})
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set())
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [jobs, setJobs] = useState<BulkAckPoJob[] | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const submittingRef = useRef(false)
  const focusKeyRef = useRef<string | null>(null)

  const reload = useCallback(async () => {
    setLoadState('loading')
    setLoadError(null)
    try {
      const next: BulkAckLoadedPo[] = []
      for (const seed of orders) {
        const [order, validation] = await Promise.all([
          client.getOrder(seed.externalOrderNumber),
          client.getValidation(seed.externalOrderNumber).catch((error: unknown) => {
            if (error instanceof VisionApiError && error.status === 404) return null
            throw error
          }),
        ])
        next.push({ order, validation: validation as ValidationRun | null })
      }
      setLoaded(next)
      setDrafts(defaultQtyDrafts(next))
      setCollapsed(new Set())
      setJobs(null)
      setLoadState('ready')
    } catch (error) {
      setLoadState('error')
      setLoadError(error instanceof Error ? error.message : 'Could not load selected POs')
    }
  }, [client, orders])

  useEffect(() => {
    if (!open) return
    void reload()
  }, [open, reload])

  const lines = useMemo(() => buildBulkAckLines(loaded, drafts), [loaded, drafts])
  const summary = useMemo(() => summarizeBulkAck(loaded, drafts), [loaded, drafts])
  const dirty = useMemo(() => draftsDifferFromDefaults(loaded, drafts), [loaded, drafts])
  const visibleLines = useMemo(
    () => lines.filter(line => !collapsed.has(line.orderId)),
    [lines, collapsed],
  )

  const requestClose = useCallback(
    (next: boolean) => {
      if (next) {
        onOpenChange(true)
        return
      }
      if (submittingRef.current) return
      if (dirty && loadState === 'ready' && !jobs) {
        const ok = window.confirm('Discard unsaved quantity edits?')
        if (!ok) return
      }
      onOpenChange(false)
    },
    [dirty, jobs, loadState, onOpenChange],
  )

  const onDraftChange = useCallback((key: string, value: string) => {
    setDrafts(prev => ({ ...prev, [key]: value }))
  }, [])

  const onPaste = useCallback(
    (key: string, text: string) => {
      setDrafts(prev => applyPastedColumn(key, text, visibleLines, prev))
    },
    [visibleLines],
  )

  const beginSubmit = useCallback(() => {
    if (summary.hasInvalid || loaded.length === 0 || submittingRef.current) return
    setConfirmOpen(true)
  }, [loaded.length, summary.hasInvalid])

  const runSubmit = useCallback(
    async (onlyRetryable: boolean) => {
      if (submittingRef.current) return
      submittingRef.current = true
      setSubmitting(true)
      setConfirmOpen(false)
      const seedJobs: BulkAckPoJob[] =
        onlyRetryable && jobs
          ? jobs
          : loaded.map(entry => ({
              orderId: entry.order.id,
              externalOrderNumber: entry.order.externalOrderNumber,
              plan: planForLoadedPo(entry, drafts),
              idempotencyKey: freshIdempotencyKey(),
              status: 'pending' as const,
            }))
      setJobs(seedJobs)
      try {
        const result = await runBulkAcknowledgements(client, seedJobs, {
          concurrency: 3,
          onlyRetryable,
          onProgress: progress => setJobs(progress.jobs),
        })
        setJobs(result)
        const succeeded = result.filter(job => job.status === 'success').length
        const retryable = result.filter(job => job.status === 'retryable').length
        const needsReview = result.filter(job => job.status === 'needs_review')
        for (const job of needsReview) {
          toast.warning(`${job.externalOrderNumber}: validation changed`, {
            description: 'Reload that PO in this dialog and review before retrying.',
          })
        }
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['orders'] }),
          queryClient.invalidateQueries({ queryKey: ['orders-summary', tenantId] }),
        ])
        if (succeeded === result.length) {
          toast.success(`Acknowledged ${num(succeeded)} POs`)
          onFinished()
          onOpenChange(false)
        } else if (succeeded > 0) {
          toast.message(`Acknowledged ${num(succeeded)} of ${num(result.length)} POs`, {
            description:
              retryable > 0 ? `${num(retryable)} can be retried.` : 'Review failures below.',
          })
        }
      } finally {
        submittingRef.current = false
        setSubmitting(false)
      }
    },
    [client, drafts, jobs, loaded, onFinished, onOpenChange, queryClient, tenantId],
  )

  const reloadOnePo = useCallback(
    async (externalOrderNumber: string) => {
      const [order, validation] = await Promise.all([
        client.getOrder(externalOrderNumber),
        client.getValidation(externalOrderNumber).catch((error: unknown) => {
          if (error instanceof VisionApiError && error.status === 404) return null
          throw error
        }),
      ])
      setLoaded(prev => {
        const next = prev.map(entry =>
          entry.order.externalOrderNumber === externalOrderNumber
            ? { order, validation: validation as ValidationRun | null }
            : entry,
        )
        setDrafts(current => {
          const defaults = defaultQtyDrafts([
            { order, validation: validation as ValidationRun | null },
          ])
          return { ...current, ...defaults }
        })
        return next
      })
      setJobs(prev =>
        prev
          ? prev.map(job =>
              job.externalOrderNumber === externalOrderNumber
                ? {
                    ...job,
                    status: 'pending',
                    message: 'Reloaded — review quantities',
                    plan: planForLoadedPo(
                      { order, validation: validation as ValidationRun | null },
                      defaultQtyDrafts([{ order, validation: validation as ValidationRun | null }]),
                    ),
                    idempotencyKey: freshIdempotencyKey(),
                  }
                : job,
            )
          : prev,
      )
    },
    [client],
  )

  const retryableCount = jobs?.filter(job => job.status === 'retryable').length ?? 0
  const showResults = jobs != null

  return (
    <>
      <Dialog open={open} onOpenChange={requestClose}>
        <DialogContent
          data-testid="bulk-ack-dialog"
          className="flex h-[min(92vh,56rem)] w-[calc(100%-1.5rem)] max-w-6xl flex-col gap-3 overflow-hidden p-4"
        >
          <DialogHeader className="shrink-0">
            <DialogTitle>Confirm new POs</DialogTitle>
            <DialogDescription>
              Edit accepted quantities, then submit each PO to Vision. Quantity 0 stays Accepted
              with quantity 0.
            </DialogDescription>
          </DialogHeader>

          <div
            data-testid="bulk-ack-summary"
            className="flex shrink-0 flex-wrap gap-3 rounded-xl bg-[color:var(--muted)]/60 px-3 py-2 text-[12px]"
          >
            <SummaryStat label="POs" value={summary.poCount} />
            <SummaryStat label="Lines" value={summary.lineCount} />
            <SummaryStat label="Units accepted" value={summary.acceptedUnits} />
            <SummaryStat label="Zero-qty" value={summary.zeroQtyLines} />
            <SummaryStat label="Validation rejected" value={summary.validationRejectedLines} />
          </div>

          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loadState !== 'ready' || submitting}
              onClick={() => setDrafts(prev => setAllEditableDrafts(loaded, prev, 'ordered'))}
            >
              Accept all
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loadState !== 'ready' || submitting}
              onClick={() => setDrafts(prev => setAllEditableDrafts(loaded, prev, 0))}
            >
              Set all to 0
            </Button>
            {showResults && retryableCount > 0 ? (
              <Button
                type="button"
                size="sm"
                disabled={submitting}
                onClick={() => void runSubmit(true)}
              >
                Retry failed ({num(retryableCount)})
              </Button>
            ) : null}
          </div>

          <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-[color:var(--border)]">
            {loadState === 'loading' ? (
              <div className="flex items-center justify-center gap-2 p-10 text-[13px] text-[color:var(--muted-foreground)]">
                <CircleNotch className="size-4 animate-spin" /> Loading selected POs…
              </div>
            ) : null}
            {loadState === 'error' ? (
              <div className="flex flex-col items-start gap-3 p-6">
                <div className="flex items-start gap-2 text-[color:var(--crit)]">
                  <WarningCircle className="mt-0.5 size-4" />
                  <div>
                    <div className="font-medium">Could not load POs</div>
                    <div className="text-[12px]">{loadError}</div>
                  </div>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => void reload()}>
                  Retry load
                </Button>
              </div>
            ) : null}
            {loadState === 'ready' ? (
              <BulkAckGrid
                loaded={loaded}
                lines={lines}
                collapsed={collapsed}
                jobs={jobs}
                submitting={submitting}
                focusKeyRef={focusKeyRef}
                onToggleCollapse={orderId =>
                  setCollapsed(prev => {
                    const next = new Set(prev)
                    if (next.has(orderId)) next.delete(orderId)
                    else next.add(orderId)
                    return next
                  })
                }
                onAcceptPo={orderId =>
                  setDrafts(prev => setAllEditableDrafts(loaded, prev, 'ordered', orderId))
                }
                onZeroPo={orderId =>
                  setDrafts(prev => setAllEditableDrafts(loaded, prev, 0, orderId))
                }
                onDraftChange={onDraftChange}
                onPaste={onPaste}
                onReloadPo={externalOrderNumber => void reloadOnePo(externalOrderNumber)}
              />
            ) : null}
          </div>

          <DialogFooter className="shrink-0">
            <Button type="button" variant="outline" onClick={() => requestClose(false)}>
              Close
            </Button>
            {!showResults || jobs?.every(job => job.status === 'pending') ? (
              <Button
                type="button"
                data-testid="bulk-ack-open-confirm"
                disabled={loadState !== 'ready' || summary.hasInvalid || submitting || loaded.length === 0}
                onClick={beginSubmit}
              >
                Review & submit
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkAckConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        loaded={loaded}
        drafts={drafts}
        pending={submitting}
        onConfirm={() => void runSubmit(false)}
      />
    </>
  )
}

function SummaryStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="min-w-[5.5rem]">
      <div className="text-[10.5px] uppercase tracking-wider text-[color:var(--muted-foreground)]">
        {label}
      </div>
      <div className="font-semibold tabular-nums text-[color:var(--foreground)]">{num(value)}</div>
    </div>
  )
}

function BulkAckGrid({
  loaded,
  lines,
  collapsed,
  jobs,
  submitting,
  focusKeyRef,
  onToggleCollapse,
  onAcceptPo,
  onZeroPo,
  onDraftChange,
  onPaste,
  onReloadPo,
}: {
  loaded: readonly BulkAckLoadedPo[]
  lines: ReturnType<typeof buildBulkAckLines>
  collapsed: ReadonlySet<string>
  jobs: BulkAckPoJob[] | null
  submitting: boolean
  focusKeyRef: React.MutableRefObject<string | null>
  onToggleCollapse: (orderId: string) => void
  onAcceptPo: (orderId: string) => void
  onZeroPo: (orderId: string) => void
  onDraftChange: (key: string, value: string) => void
  onPaste: (key: string, text: string) => void
  onReloadPo: (externalOrderNumber: string) => void
}) {
  const editableKeys = useMemo(
    () => lines.filter(line => !line.locked && !collapsed.has(line.orderId)).map(line => line.key),
    [collapsed, lines],
  )

  const focusRelative = (key: string, delta: number) => {
    const index = editableKeys.indexOf(key)
    if (index < 0) return
    const next = editableKeys[index + delta]
    if (!next) return
    focusKeyRef.current = next
    const el = document.querySelector<HTMLInputElement>(`[data-bulk-ack-key="${next}"]`)
    el?.focus()
    el?.select()
  }

  return (
    <table className="w-full min-w-[56rem] border-collapse text-left text-[12.5px]">
      <thead className="sticky top-0 z-10 bg-[color:var(--card)]">
        <tr className="border-b border-[color:var(--border)] text-[10.5px] uppercase tracking-wider text-[color:var(--muted-foreground)]">
          <th className="px-3 py-2 font-semibold">PO / FC</th>
          <th className="px-3 py-2 font-semibold">SKU / ASIN</th>
          <th className="px-3 py-2 font-semibold">Title</th>
          <th className="px-3 py-2 text-right font-semibold">Ordered</th>
          <th className="px-3 py-2 text-right font-semibold">Accepted</th>
          <th className="px-3 py-2 font-semibold">Result</th>
        </tr>
      </thead>
      <tbody>
        {loaded.map(entry => {
          const orderLines = lines.filter(line => line.orderId === entry.order.id)
          const isCollapsed = collapsed.has(entry.order.id)
          const job = jobs?.find(row => row.orderId === entry.order.id)
          return (
            <PoGroup
              key={entry.order.id}
              entry={entry}
              orderLines={orderLines}
              collapsed={isCollapsed}
              job={job}
              submitting={submitting}
              onToggleCollapse={() => onToggleCollapse(entry.order.id)}
              onAcceptPo={() => onAcceptPo(entry.order.id)}
              onZeroPo={() => onZeroPo(entry.order.id)}
              onDraftChange={onDraftChange}
              onPaste={onPaste}
              onReloadPo={() => onReloadPo(entry.order.externalOrderNumber)}
              onArrowNav={focusRelative}
            />
          )
        })}
      </tbody>
    </table>
  )
}

function PoGroup({
  entry,
  orderLines,
  collapsed,
  job,
  submitting,
  onToggleCollapse,
  onAcceptPo,
  onZeroPo,
  onDraftChange,
  onPaste,
  onReloadPo,
  onArrowNav,
}: {
  entry: BulkAckLoadedPo
  orderLines: ReturnType<typeof buildBulkAckLines>
  collapsed: boolean
  job?: BulkAckPoJob
  submitting: boolean
  onToggleCollapse: () => void
  onAcceptPo: () => void
  onZeroPo: () => void
  onDraftChange: (key: string, value: string) => void
  onPaste: (key: string, text: string) => void
  onReloadPo: () => void
  onArrowNav: (key: string, delta: number) => void
}) {
  return (
    <>
      <tr className="border-b border-[color:var(--border)] bg-[color:var(--muted)]/45">
        <td colSpan={6} className="px-3 py-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 font-semibold tabular-nums"
              onClick={onToggleCollapse}
              aria-expanded={!collapsed}
            >
              {collapsed ? <CaretRight className="size-3.5" /> : <CaretDown className="size-3.5" />}
              {entry.order.externalOrderNumber}
              <span className="font-normal text-[color:var(--muted-foreground)]">
                · FC {entry.order.shipToLocationCode ?? '—'} · {num(orderLines.length)} lines
              </span>
            </button>
            <div className="flex flex-wrap items-center gap-2">
              {job ? <JobBadge job={job} onReload={onReloadPo} /> : null}
              <Button type="button" variant="ghost" size="sm" disabled={submitting} onClick={onAcceptPo}>
                Accept all
              </Button>
              <Button type="button" variant="ghost" size="sm" disabled={submitting} onClick={onZeroPo}>
                Set to 0
              </Button>
            </div>
          </div>
        </td>
      </tr>
      {!collapsed
        ? orderLines.map(line => (
            <tr
              key={line.key}
              className={cn(
                'border-b border-[color:var(--border)]/70',
                line.locked && 'bg-[color:var(--crit-soft)]/30',
                line.invalid && 'bg-[color:var(--warn-soft)]/40',
              )}
            >
              <td className="px-3 py-1.5 tabular-nums text-[color:var(--muted-foreground)]">
                {line.externalOrderNumber}
                <div className="text-[10.5px]">{line.shipTo}</div>
              </td>
              <td className="px-3 py-1.5 font-mono text-[11.5px]">{line.channelSku}</td>
              <td className="max-w-[18rem] px-3 py-1.5">
                <div className="truncate" title={line.title}>
                  {line.title}
                </div>
                {line.rejectionReasons.length > 0 ? (
                  <div className="mt-0.5 text-[10.5px] text-[color:var(--crit)]">
                    {line.rejectionReasons.join('; ')}
                  </div>
                ) : null}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums">{num(line.orderedQty)}</td>
              <td className="px-3 py-1.5 text-right">
                {line.locked ? (
                  <span className="text-[11.5px] text-[color:var(--muted-foreground)]">—</span>
                ) : (
                  <Input
                    data-bulk-ack-key={line.key}
                    type="text"
                    inputMode="numeric"
                    value={line.draft}
                    disabled={submitting}
                    aria-invalid={line.invalid || undefined}
                    aria-label={`Accepted quantity for ${line.externalOrderNumber} line ${line.itemSequenceNumber}`}
                    className={cn(
                      'ml-auto h-8 w-20 px-2 text-right tabular-nums',
                      line.invalid && 'border-[color:var(--crit)]',
                    )}
                    onChange={event => onDraftChange(line.key, event.target.value.replace(/[^\d]/g, ''))}
                    onKeyDown={event => {
                      if (event.key === 'ArrowDown') {
                        event.preventDefault()
                        onArrowNav(line.key, 1)
                      } else if (event.key === 'ArrowUp') {
                        event.preventDefault()
                        onArrowNav(line.key, -1)
                      }
                    }}
                    onPaste={event => {
                      const text = event.clipboardData.getData('text')
                      if (!text.includes('\n') && !text.includes('\t')) return
                      event.preventDefault()
                      onPaste(line.key, text)
                    }}
                  />
                )}
              </td>
              <td className="px-3 py-1.5">
                <span
                  className={cn(
                    'text-[11.5px] font-medium',
                    line.result === 'Rejected (validation)' && 'text-[color:var(--crit)]',
                    line.result === 'Zero' && 'text-[color:var(--warn)]',
                    line.result === 'Partial' && 'text-[color:var(--warn)]',
                  )}
                >
                  {line.result}
                </span>
              </td>
            </tr>
          ))
        : null}
    </>
  )
}

function JobBadge({ job, onReload }: { job: BulkAckPoJob; onReload: () => void }) {
  const tone =
    job.status === 'success'
      ? 'text-[color:var(--ok)]'
      : job.status === 'retryable'
        ? 'text-[color:var(--warn)]'
        : job.status === 'submitting' || job.status === 'pending'
          ? 'text-[color:var(--muted-foreground)]'
          : 'text-[color:var(--crit)]'
  return (
    <span className={cn('inline-flex items-center gap-2 text-[11.5px] font-medium', tone)}>
      {job.status === 'submitting' ? <CircleNotch className="size-3 animate-spin" /> : null}
      {job.message ?? job.status}
      {job.status === 'needs_review' ? (
        <Button type="button" variant="outline" size="sm" onClick={onReload}>
          Reload PO
        </Button>
      ) : null}
    </span>
  )
}
